import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, BadgeCheck, FileCheck2, IndianRupee, MessageCircle, RefreshCcw, ShoppingCart, Tag, Quote } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import EditorialMarquee from "@/components/site/Marquee";
import BikeCard from "@/components/site/BikeCard";
import WhatsAppFloat from "@/components/site/WhatsAppFloat";
import { MaskedLine, Reveal } from "@/components/site/Reveal";
import { api } from "@/api";
import { waLink } from "@/config";

const HERO_IMG = "https://images.unsplash.com/photo-1661215477041-ec4501e00042?q=80&w=2000&auto=format&fit=crop";
const RIDER_IMG = "https://images.unsplash.com/photo-1595496172270-3956c56253dc?q=80&w=1600&auto=format&fit=crop";
const BIKE_IMG = "https://images.unsplash.com/photo-1656420731047-3eb41c9d1dee?q=80&w=1600&auto=format&fit=crop";
const SCOOTER_IMG = "https://images.unsplash.com/photo-1596036753691-6fef728e62cc?q=80&w=1600&auto=format&fit=crop";

const services = [
  {
    icon: ShoppingCart,
    num: "01",
    title: "Buy",
    desc: "Hand-picked, 120-point inspected bikes & scooters. Test ride karo, pasand aaye toh le jao — full paperwork included.",
  },
  {
    icon: Tag,
    num: "02",
    title: "Sell",
    desc: "Apni bike bechni hai? Free inspection, on-spot fair valuation aur instant payment. No haggling, no waiting.",
  },
  {
    icon: RefreshCcw,
    num: "03",
    title: "Exchange",
    desc: "Purani bike lao, nayi le jao. Best exchange value + easy upgrade on any bike in our inventory.",
  },
];

const trustPoints = [
  { icon: BadgeCheck, title: "120-Point Verified", desc: "Every vehicle passes a full mechanical & electrical inspection before listing." },
  { icon: FileCheck2, title: "Paperwork Handled", desc: "RC transfer, insurance & NOC — hum sab sambhalte hain, aap sirf sign karo." },
  { icon: IndianRupee, title: "Instant Payment", desc: "Sell karte hi payment. No delays, no deductions, no drama." },
];

const testimonials = [
  { name: "Rohit Sharma", bike: "KTM Duke 390", quote: "Price dekh ke laga too good to be true, but bike showroom condition mein mili. RC transfer bhi 3 din mein ho gaya. Full paisa-vasool deal!" },
  { name: "Priya Verma", bike: "Vespa SXL 150", quote: "First time used scooter li. Team ne sab honestly bataya — service history, minor scratches sab. Trust ban gaya. Highly recommend." },
  { name: "Amit Patel", bike: "Sold Classic 350", quote: "Apni bike bechne aaya tha, 20 minute mein valuation, same day payment. Exchange mein nayi bike bhi le gaya. Smooth process!" },
];

const Home = () => {
  const [bikes, setBikes] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);

  useEffect(() => {
    api.get("/bikes").then(({ data }) => setBikes(data.filter((b) => b.status !== "sold").slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div className="bg-[#050505] text-white" data-testid="home-page">
      <Nav />
      <WhatsAppFloat />

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden hero-spotlight" data-testid="hero-section">
        <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
          <img src={HERO_IMG} alt="Premium sports motorcycle" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/60" />
        </motion.div>

        <motion.div style={{ y: textY }} className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 pb-20 md:pb-28 w-full">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-volt uppercase tracking-[0.35em] text-xs md:text-sm font-semibold mb-5"
            data-testid="hero-eyebrow"
          >
            India's Trusted Used Two-Wheeler Studio
          </motion.p>
          <h1 className="font-display font-semibold uppercase tracking-tighter leading-[0.92] text-[13vw] md:text-[7.5rem] lg:text-[9rem]" data-testid="hero-heading">
            <MaskedLine delay={0.35}>Sell.</MaskedLine>
            <MaskedLine delay={0.5} className="text-outline">Buy.</MaskedLine>
            <MaskedLine delay={0.65} className="text-volt">Exchange.</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-6 max-w-xl text-white/70 text-base md:text-lg leading-relaxed"
          >
            Har ride ki apni kahani hoti hai. Deal 2 Wheel par apni agli kahani shuru karo — verified used bikes & scooters, fair prices, zero tension.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <Link
              to="/inventory"
              data-testid="hero-cta-inventory"
              className="inline-flex items-center gap-2 bg-volt text-black font-semibold px-7 py-3.5 rounded-full hover:scale-105 volt-glow transition-transform duration-300"
            >
              Browse Inventory <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/sell"
              data-testid="hero-cta-sell"
              className="inline-flex items-center gap-2 border border-white/30 text-white font-semibold px-7 py-3.5 rounded-full hover:border-volt hover:text-volt transition-colors duration-300"
            >
              Sell Your Bike
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6 }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
            className="w-px h-10 bg-gradient-to-b from-volt to-transparent"
          />
        </motion.div>
      </section>

      <EditorialMarquee />

      {/* SERVICES */}
      <section id="exchange" className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28" data-testid="services-section">
        <Reveal>
          <p className="text-volt uppercase tracking-[0.3em] text-xs font-semibold mb-4">What We Do</p>
          <h2 className="font-display font-semibold uppercase tracking-tighter text-4xl md:text-6xl leading-none max-w-3xl">
            Teeno kaam, <span className="text-outline">ek hi dukaan</span>
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-12">
          {services.map((s, i) => (
            <Reveal key={s.num} delay={i * 0.12}>
              <motion.div
                whileHover={{ y: -6 }}
                className="group relative border border-white/10 bg-card p-8 h-full overflow-hidden"
                data-testid={`service-card-${s.title.toLowerCase()}`}
              >
                <span className="absolute -top-3 right-4 font-display font-semibold text-7xl text-white/5 group-hover:text-volt/10 transition-colors duration-500 select-none">
                  {s.num}
                </span>
                <s.icon className="w-9 h-9 text-volt mb-6" />
                <h3 className="font-display font-semibold uppercase tracking-tight text-2xl">{s.title}</h3>
                <p className="mt-3 text-white/55 text-sm leading-relaxed">{s.desc}</p>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FEATURED INVENTORY */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28" data-testid="featured-section">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-volt uppercase tracking-[0.3em] text-xs font-semibold mb-4">Fresh Stock</p>
            <h2 className="font-display font-semibold uppercase tracking-tighter text-4xl md:text-6xl leading-none">
              Featured <span className="text-outline">Machines</span>
            </h2>
          </div>
          <Link to="/inventory" data-testid="featured-view-all" className="inline-flex items-center gap-2 text-volt font-semibold uppercase tracking-widest text-sm hover:gap-3 transition-[gap] duration-300">
            View All <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
          {bikes.map((bike, i) => (
            <BikeCard key={bike.id} bike={bike} index={i} />
          ))}
        </div>
      </section>

      {/* EXCHANGE BANNER */}
      <section className="relative overflow-hidden border-y border-white/10" data-testid="exchange-banner">
        <img src={BIKE_IMG} alt="Sports motorcycle" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/80 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-32">
          <Reveal>
            <p className="text-volt uppercase tracking-[0.3em] text-xs font-semibold mb-4">Exchange Offer</p>
            <h2 className="font-display font-semibold uppercase tracking-tighter text-4xl md:text-6xl leading-none max-w-2xl">
              Purani lao, <span className="text-volt">nayi le jao</span>
            </h2>
            <p className="mt-5 max-w-lg text-white/60 leading-relaxed">
              Apni purani bike ya scooter ka best exchange value pao — on the spot. Difference amount pay karo aur upgraded ride par nikal pado.
            </p>
            <a
              href={waLink("Hi Deal 2 Wheel! I want to exchange my old two-wheeler.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="exchange-banner-cta"
              className="mt-8 inline-flex items-center gap-2 bg-volt text-black font-semibold px-7 py-3.5 rounded-full hover:scale-105 volt-glow transition-transform duration-300"
            >
              <MessageCircle className="w-4 h-4" /> Get Exchange Value
            </a>
          </Reveal>
        </div>
      </section>

      {/* TRUST */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28" data-testid="trust-section">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="relative">
              <div className="overflow-hidden border border-white/10">
                <img src={RIDER_IMG} alt="Happy rider" className="w-full aspect-[4/5] object-cover" />
              </div>
              <div className="absolute -bottom-6 -right-4 md:-right-6 bg-volt text-black px-6 py-5" data-testid="trust-stat-badge">
                <p className="font-display font-semibold text-4xl leading-none">120+</p>
                <p className="text-xs font-semibold uppercase tracking-widest mt-1">Point Inspection</p>
              </div>
            </div>
          </Reveal>
          <div>
            <Reveal>
              <p className="text-volt uppercase tracking-[0.3em] text-xs font-semibold mb-4">Why Deal 2 Wheel</p>
              <h2 className="font-display font-semibold uppercase tracking-tighter text-4xl md:text-5xl leading-none">
                Bharosa <span className="text-outline">pehle</span>, deal baad mein
              </h2>
            </Reveal>
            <div className="mt-10 space-y-8">
              {trustPoints.map((t, i) => (
                <Reveal key={t.title} delay={i * 0.12}>
                  <div className="flex gap-5" data-testid={`trust-point-${i}`}>
                    <div className="w-12 h-12 shrink-0 border border-volt/40 bg-volt/10 flex items-center justify-center">
                      <t.icon className="w-5 h-5 text-volt" />
                    </div>
                    <div>
                      <h3 className="font-display font-semibold text-lg uppercase tracking-tight">{t.title}</h3>
                      <p className="mt-1 text-white/55 text-sm leading-relaxed">{t.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="border-t border-white/10 bg-[#070707]" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
          <Reveal>
            <p className="text-volt uppercase tracking-[0.3em] text-xs font-semibold mb-4">Riders Speak</p>
            <h2 className="font-display font-semibold uppercase tracking-tighter text-4xl md:text-6xl leading-none">
              Deal ho gayi, <span className="text-outline">khush ho gaye</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-12">
            {testimonials.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.12}>
                <div className="border border-white/10 bg-card p-7 h-full flex flex-col" data-testid={`testimonial-card-${i}`}>
                  <Quote className="w-7 h-7 text-volt mb-5" />
                  <p className="text-white/70 text-sm leading-relaxed flex-1">"{t.quote}"</p>
                  <div className="mt-6 pt-5 border-t border-white/10">
                    <p className="font-display font-semibold uppercase tracking-tight">{t.name}</p>
                    <p className="text-xs text-volt mt-0.5">{t.bike}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden" data-testid="final-cta-section">
        <img src={SCOOTER_IMG} alt="Scooter" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-[#050505]" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-36 text-left">
          <Reveal>
            <h2 className="font-display font-semibold uppercase tracking-tighter text-5xl md:text-7xl leading-[0.95]">
              Ready to <span className="text-volt">ride</span>?
            </h2>
            <p className="mt-5 max-w-md text-white/60 leading-relaxed">
              Ek message karo WhatsApp par — bike dekho, test ride karo, deal pakki karo.
            </p>
            <a
              href={waLink("Hi Deal 2 Wheel! I'm ready to make a deal.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="final-cta-whatsapp"
              className="mt-9 inline-flex items-center gap-2 bg-volt text-black font-semibold px-8 py-4 rounded-full hover:scale-105 volt-glow transition-transform duration-300"
            >
              <MessageCircle className="w-5 h-5" /> Message Us Now
            </a>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
